import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import moment from 'moment';
import { db } from '../lib/firebase';
import '../styles/users.css';
import settingDP from '../images/fiat.png';
import '@fortawesome/fontawesome-free/css/all.min.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserLock } from '@fortawesome/free-solid-svg-icons';
import { FaPlusCircle } from 'react-icons/fa';
import Loader from '../components/Loader';
import Verification from '../components/Verification';
import MarketMarquee from './MarketMarquee';

const Profile = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [updatedUser, setUpdatedUser] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [profilePictureUrl, setProfilePictureUrl] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [balance, setBalance] = useState(0);
    const fileInputRef = useRef(null);
    const [showVerificationBox, setShowVerificationBox] = useState(false);


    useEffect(() => {
        // * Function to fetch user data from Firestore
        const fetchUser = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'stock', userId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    setUser(userData);
                    setUpdatedUser(userData);
                    setProfilePictureUrl(userData.profilePictureUrl || '');

                    // Calculate the balance excluding 'Trading' account transactions
                    const userTransactions = userData.transactions || [];
                    const userBalance = userTransactions.reduce((total, transaction) => {
                        return transaction.type === 'credit'
                            ? total + transaction.amount
                            : total - transaction.amount;
                    }, 0);
                    setBalance(userBalance);

                } else {
                    console.log('No such document!');
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching user: ', error);
                setLoading(false);
            }
        };

        fetchUser();
        // Fetch user data when userId changes
    }, [userId]);

    // * change value on input
    const handleChange = (e) => {
        const { name, value } = e.target;
        setUpdatedUser({ ...updatedUser, [name]: value });
    };

    // * handle saves after editing
    const handleSave = async () => {
        try {
            await updateDoc(doc(db, 'stock', userId), updatedUser);
            setUser(updatedUser);
            setEditMode(false);
            setSuccessMessage('Update successful!');
            console.log('Update successful');
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);
        } catch (error) {
            console.error('Error updating user: ', error);
        }
    };

    // * handle forgot password
    const handleForgotPassword = async () => {
        const auth = getAuth();

        if (!user || !user.email) {
            setUploadError('User email not found.');
            return;
        }

        try {
            await sendPasswordResetEmail(auth, user.email);
            setSuccessMessage('Password reset email sent successfully to ' + user.email);
            setTimeout(() => {
                setSuccessMessage('');
            }, 8000);
        } catch (error) {
            setUploadError(`Error sending password reset email: ${error.message}`);
            console.error('Error sending password reset email:', error.message);
        }
    };

    // * state to handle the change of profile pic file
    const handleFileChange = (e) => {
        if (e.target.files[0]) {
            handleUpload(e.target.files[0]);
        }
    };

    // * state to handle upload of profile pic
    const handleUpload = async (file) => {
        const storage = getStorage();
        // Create storage reference
        const storageRef = ref(storage, `profilePictures/${userId}/${file.name}`);

        try {
            // Upload profile picture and get download url
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            // Update the Firestore document with the new profile picture URL
            await updateDoc(doc(db, 'stock', userId), { profilePictureUrl: downloadURL });
            setProfilePictureUrl(downloadURL);

            setSuccessMessage('Profile picture uploaded successfully.');
            setTimeout(() => {
                setSuccessMessage('');
            }, 5000);
        } catch (error) {
            setUploadError(`Error uploading profile picture: ${error.message}`);
            console.error('Error uploading profile picture:', error.message);
        }
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    if (loading) {
        return <div><Loader /></div>;
    }

    if (!user) {
        return <div>User not found</div>;
    }

    const userGroup = user ? user.group : 'inactive';


    return (
        <div className='pager'>
            <div className="user-infos">
                <MarketMarquee />
                <div className="user-info-container">
                    <div className="user-info-item">
                        <div className='profile-picture-container'>
                            <div className='profile-picture-wrapper'>
                                <img
                                    src={profilePictureUrl || settingDP}
                                    alt="Profile"
                                    className="profile-picture"
                                />
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                    className="file-input"
                                    style={{ display: 'none' }}
                                />
                                <FaPlusCircle
                                    className="upload-icon"
                                    onClick={() => fileInputRef.current.click()}
                                />
                            </div>
                            {uploadError && <p className="error-message">{uploadError}</p>}
                        </div></div>
                    <div className="user-info-item">
                        {/* Transaction Report and Email */}
                        <div className="user-head-item">
                            <b className="username">
                                Email: </b>
                            {user.email}{' '}
                            <i onClick={handleForgotPassword}
                                className="reset fas fa-repeat">

                            </i>
                        </div>

                        <div className="user-info-item">
                            <b className="username">Account Balance: </b>
                            ${balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>

                        {/* Show verify transaction button if deactivated */}
                        <div className="user-head-item">
                            {userGroup === 'deactivated' && (
                                <button
                                    className="verify-transactions-button"
                                    onClick={() => setShowVerificationBox(true)}
                                >  Verify Transactions
                                    <FontAwesomeIcon
                                        className="copy-icons"
                                        icon={faUserLock} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>


                <div>
                    <h3 className='username'>User Details</h3>
                    <>
                        {/* Full Name and Address */}
                        <div className="user-info-container">
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <b className="usernames">Full Name:</b>
                                        <input
                                            type="text"
                                            className='input-field'
                                            name="fullName"
                                            value={updatedUser.fullName}
                                            onChange={handleChange}
                                        />
                                    </>
                                ) : (
                                    <><b className="usernames">Full Name: </b>
                                        {user.fullName}
                                    </>
                                )}
                            </div>
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <b className="username">Address:</b>
                                        <input
                                            type="text"
                                            name="address"
                                            className='input-field'
                                            value={updatedUser.address}
                                            onChange={handleChange}
                                        />
                                    </>
                                ) : (
                                    <><b className="username">Address: </b>
                                        {user.address}
                                    </>
                                )}
                            </div>
                        </div>


                        {/* Gender and Date of Birth */}
                        <div className="user-info-container">
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <b className="username">Gender:</b>
                                        <select
                                            name="gender"
                                            className='input-field'
                                            value={updatedUser.gender}
                                            onChange={handleChange}
                                        >
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                        </select>
                                    </>
                                ) : (
                                    <>
                                        <b className="username">Gender: </b>
                                        {user.gender}
                                    </>
                                )}
                            </div>
                            <div className="user-info-item">
                                {editMode ? (
                                    <>
                                        <div>
                                            <b className="usernames"> Date of Birth:</b>
                                        </div>
                                        <input
                                            type="date"
                                            className='input-field'
                                            name="dob"
                                            value={moment(updatedUser.dob).format('YYYY-MM-DD')}
                                            onChange={handleChange}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <b className="usernames">Date of Birth: </b>
                                        {moment(user.dob).format('MMMM D, YYYY')}
                                    </>
                                )}
                            </div>
                        </div>

                    </>


                    {editMode ? (
                        // * Display if User is Editing
                        <button onClick={handleSave} className='click-button'>Save</button>
                    ) : (
                        // * else display to edit
                        <button onClick={() => setEditMode(true)} className='click-button'>Edit</button>
                    )}



                    {/* Show cover for verification box when userGroup is deactivated */}
                    {showVerificationBox && (
                        <div>
                            <Verification setShowVerificationBox={setShowVerificationBox} />
                        </div>
                    )}

                    <div className="center">
                        <button onClick={handleGoBack} className='click-button'>Go Back</button>
                    </div>

                    {successMessage && <div className="success-message"></div>}

                </div>
            </div>
        </div>
    );
};

export default Profile;
